import React, { useEffect, useState } from 'react';
import API from '../utils/api';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

const PAGE_SIZE = 25;
const MONTHS_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

export default function Visitors() {
  const [summary, setSummary] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(true);
  const [visits, setVisits] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [listLoading, setListLoading] = useState(true);

  useEffect(() => {
    API.get('/visits/admin/summary').then(r => { setSummary(r.data); setSummaryLoading(false); }).catch(() => setSummaryLoading(false));
  }, []);

  const fetchVisits = (p = page) => {
    setListLoading(true);
    API.get(`/visits/admin?page=${p}&limit=${PAGE_SIZE}`)
      .then(r => { setVisits(r.data.visits); setTotalPages(r.data.pages); setTotal(r.data.total); setPage(p); setListLoading(false); })
      .catch(() => setListLoading(false));
  };
  useEffect(() => { fetchVisits(1); }, []);

  // Built from the 'YYYY-MM-DD' string directly (not `new Date(d.date)`) so the
  // label can't shift a day depending on the viewing browser's own timezone.
  const chartData = summary?.trend?.map(d => {
    const [, m, day] = d.date.split('-');
    return { day: `${day} ${MONTHS_SHORT[Number(m) - 1]}`, Visits: d.count };
  }) || [];

  const locationLabel = v => [v.city, v.region, v.country].filter(Boolean).join(', ') || (v.country || '—');

  return (
    <div>
      <h2 className="admin-page-title">🌍 Visitor Analytics</h2>
      <p style={{ color: '#888', fontSize: '0.85rem', marginBottom: 24 }}>
        Location, network, and device details captured for each visit to the site — refresh this page any time to see the latest.
      </p>

      {summaryLoading ? <div className="loader"><div className="spinner" /></div> : !summary ? <p>Failed to load visitor stats.</p> : (
        <>
          {/* Summary Cards */}
          <div className="stats-cards" style={{ marginBottom: 32 }}>
            {[
              { label: 'Total Visits', value: summary.totalVisits, sub: 'All time', color: 'saffron' },
              { label: 'Unique Visitors', value: summary.uniqueVisitors, sub: 'Distinct browsers', color: 'green' },
              { label: "Today's Visits", value: summary.todayVisits, sub: 'Since midnight', color: 'gold' },
              { label: "This Month's Visits", value: summary.monthVisits, sub: 'Since the 1st', color: 'brown' },
            ].map((s, i) => (
              <div key={i} className={`stat-card ${s.color}`}>
                <div className="stat-card-label">{s.label}</div>
                <div className="stat-card-value">{s.value?.toLocaleString('en-IN')}</div>
                <div className="stat-card-sub">{s.sub}</div>
              </div>
            ))}
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 24, marginBottom: 32 }}>
            {/* Trend Chart */}
            <div style={{ background: '#fff', borderRadius: 12, padding: 24, boxShadow: '0 2px 16px rgba(0,0,0,0.08)' }}>
              <h3 style={{ fontWeight: 700, marginBottom: 20 }}>Visits — Last 14 Days</h3>
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={chartData} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="day" tick={{ fontSize: 12 }} />
                  <YAxis allowDecimals={false} tick={{ fontSize: 12 }} />
                  <Tooltip />
                  <Bar dataKey="Visits" fill="#FF9933" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>

            {/* Top Countries */}
            <div style={{ background: '#fff', borderRadius: 12, padding: 24, boxShadow: '0 2px 16px rgba(0,0,0,0.08)' }}>
              <h3 style={{ fontWeight: 700, marginBottom: 16 }}>Top Locations</h3>
              {summary.topCountries?.length === 0 ? <p style={{ color: '#888', fontSize: '0.9rem' }}>No location data yet</p> : (
                summary.topCountries.map((c, i) => (
                  <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #f5f5f5', fontSize: '0.9rem' }}>
                    <span>{c._id}</span>
                    <span style={{ fontWeight: 700, color: 'var(--saffron)' }}>{c.count}</span>
                  </div>
                ))
              )}
            </div>
          </div>
        </>
      )}

      {/* Recent Visits Log */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <h3 style={{ fontWeight: 700, margin: 0 }}>Recent Visits</h3>
        <span style={{ fontSize: '0.85rem', color: '#888' }}>{total.toLocaleString('en-IN')} recorded</span>
      </div>

      {listLoading ? <div className="loader"><div className="spinner" /></div> : (
        <div className="admin-table">
          <table>
            <thead><tr>
              <th>Date &amp; Time</th><th>Location</th><th>IP Address</th><th>ISP / Network</th><th>Device</th><th>Referrer</th>
            </tr></thead>
            <tbody>
              {visits.map(v => (
                <tr key={v._id}>
                  <td style={{ fontSize: '0.82rem', whiteSpace: 'nowrap' }}>{new Date(v.createdAt).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</td>
                  <td style={{ fontSize: '0.85rem' }}>{locationLabel(v)}</td>
                  <td style={{ fontSize: '0.82rem', fontFamily: 'monospace' }}>{v.ip || '—'}</td>
                  <td style={{ fontSize: '0.82rem' }}>{v.isp || '—'}</td>
                  <td style={{ fontSize: '0.82rem' }}>{[v.browser, v.os, v.device].filter(Boolean).join(' • ') || '—'}</td>
                  <td style={{ fontSize: '0.8rem', color: '#888', maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{v.referrer || 'Direct'}</td>
                </tr>
              ))}
              {visits.length === 0 && <tr><td colSpan={6} style={{ textAlign: 'center', padding: 32, color: '#888' }}>No visits recorded yet</td></tr>}
            </tbody>
          </table>
          {totalPages > 1 && (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 16, padding: '16px 0' }}>
              <button className="btn btn-sm btn-outline" disabled={page <= 1} onClick={() => fetchVisits(page - 1)}>← Prev</button>
              <span style={{ fontSize: '0.9rem', color: '#555' }}>Page {page} of {totalPages}</span>
              <button className="btn btn-sm btn-outline" disabled={page >= totalPages} onClick={() => fetchVisits(page + 1)}>Next →</button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
